import React, { useContext, useState } from "react";
import {
  CnftDisplay,
  CreateCNFTForm,
  FormData,
  MintProgress,
} from "../components";
import { Button } from "react-bootstrap";
import {
  encryptAndMint,
  ifString,
  KeyserverStorage,
  ProgressReport,
  SepoliaChain,
  Step,
} from "../client";
import { AppContext } from "../AppContext";
import { RequireMetaMask } from "../components/display";
import { IpfsStorage, S3Storage } from "../client/Storage";

enum State {
  DataEntry,
  Preview,
  Minting,
}

interface MintTxn {
  report: ProgressReport;
  name: string;
}

// TODO: Ideally there would be a way for the user to specify what should go
// where. For now I've chosen the way the previous code did it.
export function MintPage() {
  const ctx = useContext(AppContext);
  const [state, setState] = useState(State.DataEntry);
  const [data, setData] = useState<FormData>({
    metadata: {},
    chain: "Sepolia",
    storage: "Backblaze B2",
    ipfs: {
      pinata: false,
    },
  });
  // NOTE: We may want this stored across all pages, otherwise this information
  // is lost when visiting another page.
  const [mints, setMints] = useState<MintTxn[]>([]);

  // WARNING: Do not pass this to a function that executes this asynchronously
  // or in paralell, otherwise `index` breaks.
  const mint = async (nextState: State) => {
    // TODO: Use data.chain and data.storage
    let storage;
    switch (data.storage) {
      case "Keyserver":
        storage = new KeyserverStorage();
        break;
      case "IPFS":
        storage = new IpfsStorage(data.ipfs);
        break;
      case "Backblaze B2":
        storage = new S3Storage();
        break;
      default:
        console.error("Unknown storage selected", data.storage);
        break;
    }
    let chain;
    switch (data.chain) {
      case "Sepolia":
        chain = new SepoliaChain(await ctx.provider.getSigner());
        break;
      default:
        console.error("Unknown chain selected", data.storage);
        break;
    }

    // NOTE: Storing the index here only works as long as the array is never
    // shortened.
    const index = mints.length;

    const cname1 = data.metadata.name$confidential;
    const v = data.metadata.$confidential;
    const cname2 =
      v && typeof v === "object" && "name" in v && typeof v?.name === "string"
        ? v?.name
        : undefined;

    setMints([
      ...mints,
      {
        name: ifString(cname1 ?? cname2 ?? data.metadata.name) ?? "[Unnamed]",
        report: { step: Step.KeyReservation },
      },
    ]);
    setState(nextState);

    await encryptAndMint(data.metadata, storage!, chain!, (report) => {
      console.log("Report:", report);
      // Must be a closure, otherwise this closure has a stale view of mints.
      setMints((old) =>
        old.map((v, i) =>
          i === index
            ? {
                name: v.name,
                report,
              }
            : v,
        ),
      );
    });
  };

  const buttons = (
    <div>
      {state === State.Preview && (
        <Button className="button" onClick={() => setState(State.DataEntry)}>
          Edit
        </Button>
      )}
      {state === State.DataEntry && (
        <Button className="button" onClick={() => setState(State.Preview)}>
          Show Preview
        </Button>
      )}
      {state === State.Minting && (
        <Button className="button" onClick={() => setState(State.DataEntry)}>
          Create new
        </Button>
      )}
      {state !== State.Minting && (
        <RequireMetaMask>
          <Button
            className="button sends-txn"
            onClick={async () => mint(State.Minting)}
          >
            Mint
          </Button>
          <Button
            className="button sends-txn"
            onClick={async () => mint(State.DataEntry)}
          >
            Mint + Create new
          </Button>
        </RequireMetaMask>
      )}
    </div>
  );

  const transactions = mints
    .map((txn, i) => (
      <MintProgress key={i} report={txn.report} name={txn.name} />
    ))
    .toReversed();

  return (
    <div className="sidebar">
      <div>
        {state === State.DataEntry ? (
          <CreateCNFTForm state={[data, setData]} />
        ) : (
          <CnftDisplay metadata={data.metadata} />
        )}
        {buttons}
      </div>
      <div className="transaction-list">
        <h3>Current Transactions</h3>
        {transactions}
      </div>
    </div>
  );
}
